import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { serviceLineMap, subCategoryServiceLineToNavMenuUrlMap } from '../utils/service-types-helpers';
import Typography from '../primitives/typography';
import { breakpoints } from '../primitives/tokens';
import Accordion, { AccordionButton, AccordionIcon, AccordionPanel } from '../primitives/accordion';
import Link from '../primitives/link';
import { colors } from '../primitives/colors';

type ServiceType = unknown & {
    serviceLine: string
    sort: number
};
interface ServiceTypesTextProps {
    heading?: string
    className?: string
    permalink?: string
    servicesProvidedReadable: ServiceType[]
    small?: boolean
}

const ServiceTypesText = ({
    servicesProvidedReadable,
    heading,
    className,
    permalink = '',
    small,
}: ServiceTypesTextProps): JSX.Element | null => {
    const {
        allCmsServiceTypes: {
            nodes: allServiceTypes,
        },
    } = useStaticQuery<Queries.AllServiceTypesTextQuery>(graphql`
        query AllServiceTypesText {
            allCmsServiceTypes {
                nodes {
                    title
                    sort
                    icon {
                        title
                        url
                    }
                    franchise_link {
                        href
                    }
                    franchise_service_mapping
                }
            }
        }
    `);

    if (!servicesProvidedReadable || servicesProvidedReadable.length === 0) {
        return null;
    }

    const formattedServiceTypes = servicesProvidedReadable
        .filter(x => !allServiceTypes.find(y => x.serviceLine === y.franchise_service_mapping))
        .sort((a, b) => a.sort - b.sort);

    if (formattedServiceTypes.length === 0) {
        return null;
    }

    return (
        <div className={className}>
            {heading && (
                <Typography as="h4" fontWeight="medium">
                    {heading}
                </Typography>
            )}
            <ul css={{
                [breakpoints.tb]: {
                    display: small ? 'none' : 'unset',
                },
            }}
            >
                {formattedServiceTypes.map((item, id) => {
                    const href = subCategoryServiceLineToNavMenuUrlMap[item.serviceLine];

                    if (href && permalink) {
                        return (
                            <li
                                css={{
                                    display: 'inline',
                                    '&:after': {
                                        content: "', '",
                                    },
                                    '&:last-child:after': {
                                        content: "''",
                                    },
                                }}
                                key={id}
                            >
                                <Link
                                    css={{
                                        textDecoration: 'underline',
                                        transition: '0.25s',
                                        '&:hover': {
                                            color: colors.primaryGreen,
                                        },
                                    }}
                                    to={(permalink + href)}
                                >
                                    {serviceLineMap[item.serviceLine] || item.serviceLine}
                                </Link>
                            </li>
                        );
                    }

                    return (
                        <li
                            css={{
                                display: 'inline',
                                '&:after': {
                                    content: "', '",
                                },
                                '&:last-child:after': {
                                    content: "''",
                                },
                            }}
                            key={id}
                        >
                            {serviceLineMap[item.serviceLine] || item.serviceLine}
                        </li>
                    );
                })}
            </ul>
            {small && (
                <div css={{
                    display: 'none',
                    [breakpoints.tb]: {
                        display: 'block',
                    },
                }}
                >
                    <Accordion>
                        <AccordionButton>
                            <Typography
                                css={{
                                    textDecoration: 'underline',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                                color="primaryOrange"
                            >
                                Show all Services
                                <AccordionIcon css={{
                                    marginLeft: '8px',
                                }}
                                />
                            </Typography>
                        </AccordionButton>
                        <AccordionPanel>
                            <ul>
                                {servicesProvidedReadable.map((item, id) => {
                                    const href = subCategoryServiceLineToNavMenuUrlMap[item.serviceLine];

                                    if (href && permalink) {
                                        return (
                                            <li
                                                css={{
                                                    display: 'inline',
                                                    '&:after': {
                                                        content: "', '",
                                                    },
                                                    '&:last-child:after': {
                                                        content: "''",
                                                    },
                                                }}
                                                key={id}
                                            >
                                                <Link
                                                    css={{
                                                        textDecoration: 'underline',
                                                        transition: '0.25s',
                                                        '&:hover': {
                                                            color: colors.primaryGreen,
                                                        },
                                                    }}
                                                    to={(permalink + href)}
                                                >
                                                    {serviceLineMap[item.serviceLine] || item.serviceLine}
                                                </Link>
                                            </li>
                                        );
                                    }

                                    return (
                                        <li
                                            css={{
                                                display: 'inline',
                                                '&:after': {
                                                    content: "', '",
                                                },
                                                '&:last-child:after': {
                                                    content: "''",
                                                },
                                            }}
                                            key={id}
                                        >
                                            {serviceLineMap[item.serviceLine] || item.serviceLine}
                                        </li>
                                    );
                                })}
                            </ul>
                        </AccordionPanel>
                    </Accordion>
                </div>
            )}
        </div>
    );
};

export default ServiceTypesText;
